import React, { useEffect, useMemo, useState } from 'react';
import { blogWordpressCategoriesService, WordpressBlogCategory } from '../../../../services/blogWordpressCategoriesService';
import { blogStagesService } from '../../../../services/blogStagesService';
import { ArticleContextProvider, useArticleContext } from '../../ArticleContext';
import { ArticleDocumentEditor } from '../../ArticleDocumentEditor';
import {
  HIDDEN_FROM_UI_STAGE_IDS,
  isDisabledStageId,
  isImplementedStageId,
  sortPipelineStages,
} from '../../stageRegistry';
import { StageSetupV2 } from '../../stages/StageSetupV2';
import { RunControls } from './RunControls';
import { PipelineSidebar } from './PipelineSidebar';
import { ArticleList } from './ArticleList';
import { useArticles } from './useArticles';
import { STAGE_COMPONENTS } from './stageComponents';
import type { PipelineStage, PipelineDoc } from './articleTypes';

// ---------------------------------------------------------------------------
// EditorOrStageContent — toggles between document editor and stage view
// ---------------------------------------------------------------------------

function EditorOrStageContent({ children }: { children: React.ReactNode }) {
  const { editorOpen } = useArticleContext();
  if (editorOpen) {
    return <ArticleDocumentEditor />;
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const REFRESH_STAGE_ORDER = ['refresh_1_audit', 'refresh_2_serp', 'refresh_3_competitor_extract', 'refresh_4_competitor_synthesize', 'refresh_5_links', 'refresh_6_brief', 'refresh_7_research', 'refresh_8_drafting', 'refresh_9_assembly', 'refresh_9b_image', 'refresh_10_cta', 'refresh_11_seo', 'refresh_11b_editorial_review', 'refresh_11c_polish', 'refresh_12_publish'];

export const ArticleGeneration: React.FC<{
  initialArticleId?: string | null;
  onArticleSelected?: (candidateId: string | null) => void;
}> = ({ initialArticleId, onArticleSelected }) => {
  // ---- Core state ----
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<PipelineDoc | null>(null);
  const [showPublishedArticles, setShowPublishedArticles] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(initialArticleId ?? null);
  const [selectedArticleStageId, setSelectedArticleStageId] = useState<string | null>(null);
  const [wordpressCategories, setWordpressCategories] = useState<WordpressBlogCategory[]>([]);

  // ---- Articles hook ----
  const { articles, articlesLoading, articlesError, setArticlesError, loadArticles } = useArticles(showPublishedArticles);

  // Sync initialArticleId changes from parent
  useEffect(() => {
    if (initialArticleId) setSelectedArticleId(initialArticleId);
  }, [initialArticleId]);

  useEffect(() => {
    onArticleSelected?.(selectedArticleId);
  }, [selectedArticleId, onArticleSelected]);

  // Clear selection if article no longer in list
  useEffect(() => {
    if (selectedArticleId && articles.length > 0 && !articles.find(row => row.candidate.candidate_id === selectedArticleId)) {
      setSelectedArticleId(null);
      setSelectedArticleStageId(null);
    }
  }, [articles, selectedArticleId]);

  // ---- Pipeline loading ----
  useEffect(() => {
    (async () => {
      setPipelineLoading(true);
      try {
        const { stages: configStages } = await blogStagesService.list('active');
        const pipelineStages: PipelineStage[] = (configStages || [])
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
          .map((s: any) => ({
            id: s.stage_id,
            name: s.name,
            order: s.order,
            type: s.type,
            depends_on: s.depends_on,
            human_checkpoint: s.human_checkpoint,
            prompt: s.prompt_template ? {
              model: s.model,
              temperature: s.temperature,
              max_tokens: s.max_tokens,
              system_prompt: s.system_prompt,
              template: s.prompt_template,
              output_schema: s.output_schema,
              thinking: s.thinking,
              output_config: s.output_config,
            } : undefined,
          }));
        setPipeline({
          pipeline: { id: 'blog_article_v4', name: 'Blog Article Creation Pipeline v4', version: 'v4' },
          stages: pipelineStages,
        });
      } catch (e: any) {
        setPipelineError(e?.message || 'Failed to load pipeline config');
      } finally {
        setPipelineLoading(false);
      }
    })();
  }, []);

  // ---- WordPress categories ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await blogWordpressCategoriesService.list();
        if (!cancelled) setWordpressCategories(res.categories);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- Derived pipeline data ----
  const allStages = useMemo(() => sortPipelineStages(pipeline?.stages || []), [pipeline]);

  const selectedArticle = articles.find(row => row.candidate.candidate_id === selectedArticleId) || null;
  const selectedPipelineType = selectedArticle?.run?.pipeline_type || 'creation';

  const stages = useMemo(() => {
    if (selectedPipelineType === 'refresh') {
      const stageById = new Map(allStages.map((s) => [s.id, s]));
      return REFRESH_STAGE_ORDER
        .map((id) => stageById.get(id))
        .filter((s): s is NonNullable<typeof s> => s != null);
    }
    return allStages.filter((s) => {
      if (HIDDEN_FROM_UI_STAGE_IDS.includes(s.id as (typeof HIDDEN_FROM_UI_STAGE_IDS)[number])) return false;
      return !s.id.startsWith('refresh_');
    });
  }, [allStages, selectedPipelineType]);

  const firstStageId = stages[0]?.id;
  const currentStageId = selectedArticle?.currentStageId || firstStageId || null;
  const activeStageId = selectedArticleStageId || currentStageId;
  const activeStage = stages.find(stage => stage.id === activeStageId) || null;

  const bannerError = [pipelineError, articlesError].filter(Boolean).join(' \u2022 ');

  // ---- Render ----
  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="border-b border-border bg-gray-50">
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-purple-900 mb-4">Content Management</h1>
            <div className="flex items-center gap-3 mb-4">
              {(pipelineLoading || articlesLoading) && <div className="text-sm text-gray-500">Loading...</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {bannerError && (
        <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{bannerError}</p>
        </div>
      )}

      <div className="p-6 flex-1 overflow-y-auto">
        {/* ---- Article list view (no article selected) ---- */}
        {!selectedArticle && (
          <ArticleList
            articles={articles}
            articlesLoading={articlesLoading}
            showPublishedArticles={showPublishedArticles}
            setShowPublishedArticles={setShowPublishedArticles}
            wordpressCategories={wordpressCategories}
            onSelectArticle={(id) => { setSelectedArticleId(id); }}
            onNewArticleCreated={(id) => { setSelectedArticleId(id); setSelectedArticleStageId('__setup__'); }}
            onArticlesError={(msg) => setArticlesError(msg)}
            loadArticles={loadArticles}
          />
        )}

        {/* ---- Article detail view (article selected) ---- */}
        {selectedArticle && (
          <ArticleContextProvider
            candidate={selectedArticle.candidate}
            run={selectedArticle.run ?? null}
            onArticlesRefresh={loadArticles}
          >
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Header row */}
                <div className="lg:col-span-12">
                  <div className="flex items-center mb-4">
                    <button onClick={() => { setSelectedArticleId(null); setSelectedArticleStageId(null); }} className="text-sm text-gray-500 hover:text-gray-700 shrink-0">&larr; Back to articles</button>
                    <h2 className="text-lg font-semibold text-gray-900 text-center flex-1">{selectedArticle.candidate.candidate_name}</h2>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center bg-gray-100 rounded-md p-0.5 text-xs">
                        <button type="button" className={`px-3 py-1 rounded transition-all ${selectedArticleStageId === '__document__' ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setSelectedArticleStageId('__document__')}>Document</button>
                        <button type="button" className={`px-3 py-1 rounded transition-all ${!selectedArticleStageId || selectedArticleStageId !== '__document__' ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setSelectedArticleStageId(currentStageId)}>Stages</button>
                      </div>
                      <RunControls />
                    </div>
                  </div>
                </div>

                {/* Stage navigator (left sidebar) */}
                <div className="lg:col-span-3">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pipeline</h3>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                    {/* Article Setup card */}
                    {(() => {
                      const isSetupSelected = activeStageId === '__setup__';
                      const hasStarted = (selectedArticle?.run?.stages || []).some((s: { status: string }) => s.status === 'succeeded');
                      const navy = '#1a2332';
                      const green = '#16a34a';
                      const cyan = '#01bcd4';
                      return (
                        <div className="relative pl-8 mb-3">
                          <div className="absolute left-[11px] top-5 w-[10px] h-[10px] rounded-full z-10 border-2" style={{ backgroundColor: isSetupSelected ? cyan : hasStarted ? green : navy, borderColor: '#f5f5f7' }} />
                          <div className={`relative rounded-xl bg-white p-3 cursor-pointer transition-all duration-150 max-w-[260px] ${isSetupSelected ? 'shadow-lg' : 'hover:shadow-md'}`} style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: isSetupSelected ? cyan : hasStarted ? green : navy }} onClick={() => setSelectedArticleStageId('__setup__')}>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono font-bold uppercase" style={{ color: isSetupSelected ? cyan : hasStarted ? green : '#94a3b8' }}>&#9670;</span>
                              <span className="text-[13px] font-semibold leading-tight" style={{ color: isSetupSelected ? '#0e7490' : hasStarted ? '#374151' : '#64748b' }}>Article Setup</span>
                            </div>
                            <div className="mt-1 text-[11px]" style={{ color: hasStarted ? green : '#9ca3af' }}>{hasStarted ? 'Configured' : 'Ready'}</div>
                          </div>
                        </div>
                      );
                    })()}

                    <PipelineSidebar stages={stages} activeStageId={activeStageId} onSelectStage={setSelectedArticleStageId} />
                  </div>
                </div>

                {/* Center panel */}
                <div className="lg:col-span-9">
                  {selectedArticleStageId === '__document__' && <ArticleDocumentEditor />}
                  {selectedArticleStageId === '__setup__' && (
                    <div className="bg-white border rounded p-4">
                      <StageSetupV2 wordpressCategories={wordpressCategories} />
                    </div>
                  )}
                  {selectedArticleStageId !== '__document__' && selectedArticleStageId !== '__setup__' && (
                    <EditorOrStageContent>
                      <div className="bg-white border rounded p-4">
                        {!activeStage && <div className="text-sm text-gray-500">Select a stage to view details.</div>}
                        {activeStage && !isImplementedStageId(activeStage.id) && !STAGE_COMPONENTS[activeStage.id] && (
                          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {isDisabledStageId(activeStage.id) ? 'This stage is intentionally disabled.' : 'Stage execution UI coming next.'}
                          </div>
                        )}
                        {activeStage && (() => {
                          const StageComponent = STAGE_COMPONENTS[activeStage.id];
                          return StageComponent ? <StageComponent /> : null;
                        })()}
                      </div>
                    </EditorOrStageContent>
                  )}
                </div>
              </div>
            </div>
          </ArticleContextProvider>
        )}
      </div>
    </div>
  );
};
